import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { X, Download, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function XlsxPreviewModal({ open, onClose, xlsxUrl, xlsxName }) {
  const [sheets, setSheets] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && xlsxUrl) {
      loadXlsx();
    }
    return () => {
      setSheets([]);
      setActiveSheet(0);
      setError(null);
    };
  }, [open, xlsxUrl]);

  const loadXlsx = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${BACKEND_URL}${xlsxUrl}`);
      if (!response.ok) throw new Error('Failed to fetch file');
      const buffer = await response.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });

      const parsed = workbook.SheetNames.map((name) => {
        const sheet = workbook.Sheets[name];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        return { name, rows: json };
      });

      setSheets(parsed);
      setActiveSheet(0);
    } catch (err) {
      console.error('XLSX parse error:', err);
      setError('Could not load spreadsheet. Try downloading instead.');
    } finally {
      setLoading(false);
    }
  };

  const current = sheets[activeSheet];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border border-zinc-800 max-w-[95vw] w-[1100px] max-h-[90vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex-1 min-w-0">
            <DialogHeader>
              <DialogTitle className="font-heading font-bold text-lg truncate text-white" data-testid="xlsx-preview-title">
                {xlsxName || 'Spreadsheet Preview'}
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <a
              href={`${BACKEND_URL}${xlsxUrl}`}
              download
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 hover:bg-green-500 text-white transition-colors font-medium"
              data-testid="xlsx-preview-download"
            >
              <Download size={14} />
              Download
            </a>
          </div>
        </div>

        {/* Sheet Tabs */}
        {sheets.length > 1 && (
          <div className="flex items-center gap-1 px-5 py-2 border-b border-zinc-800 overflow-x-auto" data-testid="xlsx-sheet-tabs">
            {sheets.map((sheet, i) => (
              <button
                key={sheet.name || `sheet-${i}`}
                onClick={() => setActiveSheet(i)}
                className={`px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                  i === activeSheet
                    ? 'bg-green-600 text-white'
                    : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200'
                }`}
                data-testid={`xlsx-sheet-tab-${i}`}
              >
                {sheet.name}
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto min-h-0">
          {loading && (
            <div className="flex items-center justify-center py-20" data-testid="xlsx-preview-loading">
              <Loader2 className="w-8 h-8 animate-spin text-green-500" />
              <span className="ml-3 text-zinc-400">Loading spreadsheet...</span>
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="xlsx-preview-error">
              <p className="text-red-400 mb-3">{error}</p>
              <a
                href={`${BACKEND_URL}${xlsxUrl}`}
                download
                className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium transition-colors"
              >
                Download File Instead
              </a>
            </div>
          )}

          {!loading && !error && current && (
            <div className="p-4" data-testid="xlsx-preview-table-container">
              {current.rows.length === 0 ? (
                <p className="text-zinc-500 text-center py-10">This sheet is empty</p>
              ) : (
                <table className="w-full text-sm border-collapse" data-testid="xlsx-preview-table">
                  <thead>
                    {current.rows.length > 0 && (
                      <tr>
                        {current.rows[0].map((cell, ci) => (
                          <th
                            key={`${current.name}-h-${ci}`}
                            className="bg-zinc-800 text-zinc-200 font-bold text-left px-3 py-2.5 border border-zinc-700 whitespace-nowrap sticky top-0"
                          >
                            {cell !== null && cell !== undefined ? String(cell) : ''}
                          </th>
                        ))}
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {current.rows.slice(1).map((row, ri) => (
                      <tr key={`${current.name}-r-${ri}`} className={ri % 2 === 0 ? 'bg-zinc-900/50' : 'bg-zinc-950'}>
                        {current.rows[0].map((_, ci) => (
                          <td
                            key={`${current.name}-r${ri}-c${ci}`}
                            className="px-3 py-2 border border-zinc-800 text-zinc-300 whitespace-nowrap"
                          >
                            {row[ci] !== null && row[ci] !== undefined ? String(row[ci]) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="text-xs text-zinc-600 mt-3 text-right">
                {current.rows.length > 0 ? `${current.rows.length - 1} rows` : '0 rows'}
                {sheets.length > 1 ? ` · Sheet ${activeSheet + 1} of ${sheets.length}` : ''}
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
